import { useNavigate, useLocation } from 'react-router-dom';
import {
  Briefcase,
  Users,
  Shield,
  Menu,
  LayoutDashboard,
  FileText,
  Settings,
  Upload,
  ClipboardList,
  UserCog,
  Network,
  Mail,
  BookUser,
  FileStack,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores';
import { usePermissions } from '@/hooks/use-permissions';

// Navigation items — 'requiresEdit' flag hides the item for view-only users
const navItems = [
  { id: 'dashboard', label: 'DASHBOARD', icon: LayoutDashboard, path: '/app' },
  { id: 'jobs', label: 'JOBS', icon: Briefcase, path: '/app/jobs' },
  { id: 'insureds', label: 'INSUREDS', icon: Users, path: '/app/insureds' },
  { id: 'risk-reqs', label: 'INS REQS', icon: Shield, path: '/app/risk-requirements' },
  { id: 'upload', label: 'UPLOAD COI', icon: Upload, path: '/app/documents/upload', requiresEdit: true },
  { id: 'tasks', label: 'TASKS', icon: ClipboardList, path: '/app/tasks' },
  { id: 'notices', label: 'NOTICES SENT', icon: Mail, path: '/app/notifications' },
  { id: 'templates', label: 'TEMPLATES', icon: FileStack, path: '/app/templates' },
  { id: 'directory', label: 'DIRECTORY', icon: BookUser, path: '/app/directory' },
  { id: 'team', label: 'TEAM', icon: UserCog, path: '/app/team' },
  { id: 'departments', label: 'DEPTS', icon: Network, path: '/app/departments' },
];

const bottomNavItems = [
  { id: 'reports', label: 'REPORTS', icon: FileText, path: '/app/reports' },
  { id: 'settings', label: 'SETTINGS', icon: Settings, path: '/app/settings' },
];

/**
 * Application sidebar component.
 * Contains main navigation items.
 */
export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSidebarCollapsed, toggleSidebar } = useUIStore();
  const { canEdit } = usePermissions();

  const isActive = (path: string) => {
    if (path === '/app') {
      return location.pathname === '/app';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-16 z-30 flex h-[calc(100vh-4rem)] flex-col',
        'border-r bg-white transition-all duration-300',
        isSidebarCollapsed ? 'w-16' : 'w-60'
      )}
    >
      {/* Toggle Button */}
      <button
        onClick={toggleSidebar}
        className="absolute -right-3 top-4 z-50 flex h-6 w-6 items-center justify-center rounded-full border bg-white shadow-sm hover:bg-gray-50"
      >
        <Menu className="h-4 w-4 text-gray-500" />
      </button>

      {/* Main Navigation */}
      <nav className="flex-1 overflow-y-auto p-3">
        <ul className="space-y-1">
          {navItems.filter((item) => !item.requiresEdit || canEdit).map((item) => (
            <li key={item.id}>
              <button
                onClick={() => navigate(item.path)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive(item.path)
                    ? 'bg-coilive-blue text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                )}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {!isSidebarCollapsed && <span>{item.label}</span>}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Bottom Navigation */}
      <div className="border-t p-3">
        <ul className="space-y-1">
          {bottomNavItems.map((item) => (
            <li key={item.id}>
              <button
                onClick={() => navigate(item.path)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive(item.path)
                    ? 'bg-coilive-blue text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                )}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {!isSidebarCollapsed && <span>{item.label}</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}
